import { functions } from '../firebase/client';
import { httpsCallable } from 'firebase/functions';
import emailjs from '@emailjs/browser';
import emailjsConfig from './emailjsConfig';

class EmailService {
  constructor() {
    this.sendEmail = httpsCallable(functions, 'sendEmail');
    this.emailServiceId = emailjsConfig.serviceId;
    this.emailTemplateId = emailjsConfig.templateId;
    this.emailPublicKey = emailjsConfig.publicKey;
  }

  async sendBookingConfirmation(bookingData) {
    try {
      const { userEmail, userName, branchName, date, timeSlot, totalPrice, bookingId, paymentId, addOns } = bookingData;

      const formattedDate = new Date(date).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      // Try to use EmailJS first
      try {
        // Prepare data for email template
        // Ensure all fields required by the template are present and correctly formatted
        const basePrice = bookingData.basePrice || 0;
        const addOnsTotal = bookingData.addOnsPrice || bookingData.addOnsTotal || 0;
        const extraGuestCharge = bookingData.extraGuestCharge || 0;
        const totalAmount = bookingData.totalPrice || (basePrice + addOnsTotal + extraGuestCharge);
        const advancePayment = bookingData.advancePayment || 0;
        const balanceAmount = bookingData.balanceAmount || 0;
        const isCOD = bookingData.paymentMethod === 'cod';
        const status = bookingData.status.charAt(0).toUpperCase() + bookingData.status.slice(1);

        // Format add-ons for the template
        const formattedAddOns = (addOns || []).map(addon => ({
          name: addon.name,
          price: this._formatCurrency(addon.price)
        }));

        // Create template parameters
        const templateParams = {
          userEmail: userEmail,
          id: bookingId,
          branchName: branchName,
          date: formattedDate,
          timeSlot: timeSlot,
          basePrice: this._formatCurrency(basePrice),
          addOnsTotal: this._formatCurrency(addOnsTotal),
          totalAmount: this._formatCurrency(totalAmount),
          status: status,
          addOns: formattedAddOns,
          isCOD: isCOD,
          advancePayment: this._formatCurrency(advancePayment),
          balanceAmount: this._formatCurrency(balanceAmount),
        };

        console.log('Sending email with EmailJS:', templateParams);

        const result = await emailjs.send(
          this.emailServiceId,
          this.emailTemplateId,
          templateParams
        );

        console.log('EmailJS SUCCESS:', result.text);
        return { success: true, provider: 'emailjs' };
      } catch (emailjsError) {
        console.warn('EmailJS failed, falling back to Firebase:', emailjsError);

        // Fallback to Firebase functions
        const emailData = {
          to: userEmail,
          template: 'booking_confirmation',
          data: {
            userName,
            branchName,
            date: formattedDate,
            timeSlot,
            totalPrice: this._formatCurrency(totalPrice),
            bookingId,
            paymentId,
            addOns: bookingData.addOns || []
          }
        };

        const result = await this.sendEmail(emailData);
        return { ...result.data, provider: 'firebase' };
      }
    } catch (error) {
      console.error('Error sending confirmation email:', error);
      throw error;
    }
  }

  async sendPaymentReceipt(paymentData) {
    try {
      const { userEmail, userName, amount, paymentId, orderId, items } = paymentData;

      const emailData = {
        to: userEmail,
        template: 'payment_receipt',
        data: {
          userName,
          amount: this._formatCurrency(amount),
          paymentId,
          orderId,
          items,
          date: new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })
        }
      };

      const result = await this.sendEmail(emailData);
      return result.data;
    } catch (error) {
      console.error('Error sending payment receipt:', error);
      throw error;
    }
  }

  _formatCurrency(amount) {
    return amount.toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }
}

export default new EmailService(); 